import { AnimatedHeight } from '@codezo/offer-extras';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type {
	OfferDeliveryGroup,
	OfferDeliveryRow,
	OfferDeliverySplitOption,
	ProductRef,
} from '../types';
import { Layers } from 'lucide-react';
import { MicroLabel, RadioMark, cn } from '../ui/primitives';

/**
 * How each product could ship, priced per quantity tier.
 *
 * Shipping is quoted against a quantity — 500 totes cost more to move than 250 —
 * so a product with two priced tiers has two rows to its name, and only one of
 * them applies at a time. What shows by default is the row for the tier the
 * client is on; the rest sit behind a disclosure.
 *
 * Both halves of that are deliberate. Showing every tier at once turns a
 * three-product offer into nine rows of mostly-inapplicable prices. But dropping
 * the others entirely would mean changing the selected quantity just to find out
 * what shipping it costs — reading a price by making a change. The disclosure
 * keeps the comparison one click away without paying for it on every row.
 */
export function OfferDelivery({
	groups,
	emptyLabel = 'No products to ship yet.',
}: {
	groups: OfferDeliveryGroup[];
	emptyLabel?: string;
}) {
	const hasActions = groups.some((group) => group.rows.some((row) => row.actions));
	/* One column for the whole table, not one per group: a chevron that appears on
	   some rows and not others puts the Method buttons at two different right
	   edges. */
	const hasAlternates = groups.some((group) => group.rows.length > 1);
	/* Transit time rides under the arrival badge rather than holding a column of
	   its own. It is the same fact twice — "10–14 d" is how the date beneath the
	   badge was arrived at — and a 92px column plus its gutter is a tenth of this
	   table spent restating it, on the one row where width is scarce.
	 *
	 * ── Where the spare width goes ──
	 *
	 * Into all three, in proportion. A card is routinely 300px wider than this row
	 * needs, and that space has to land somewhere: spread across the columns it
	 * reads as a table with room, but pooled into one it reads as a mistake. Both
	 * ways of pooling it are in this line's history — content-sized name and
	 * arrival made the methods stretch to 380px an option with the price exiled to
	 * the far edge; a fractional name made "test" sit alone in 340px.
	 *
	 * So they stay fractions. What was actually wrong was never the ratios but the
	 * method track's floor: it can't shrink under the two buttons in it, and it
	 * used to.
	 *
	 * That floor is what the buttons measure, not a round number. Per option: 19px
	 * of padding + 16px radio + two 6px gaps + ~50px for "Standard" + ~94px for a
	 * 13-character price in 12px mono, which carries "DKK 1,234,567". Two of those
	 * plus the 6px between them is 400. The 240 it started at left the label
	 * NEGATIVE space once the radio and the price had taken theirs, so every
	 * method read "Stan…" unless the card was wide; 350 covered the label but not
	 * a price past six figures, which then ran out through the button's border. */
	const columns = `minmax(0,1.1fr) minmax(0,1fr) minmax(400px,2fr)${hasActions ? ' 32px' : ''}${hasAlternates ? ' 28px' : ''}`;

	if (groups.length === 0) {
		return <div className="px-4 py-3 text-sm text-text-4">{emptyLabel}</div>;
	}

	return (
		/* What the row needs with an ordinary product name and arrival date: the
		   method floor, the two content columns at their usual size, the gutters.
		   Long names and open alternates push past it, and then the table scrolls
		   — the honest failure, since a row that scrolls can still be read. */
		<div className="min-w-[720px] px-4 pb-3">
			<div className="grid gap-x-5" style={{ gridTemplateColumns: columns }}>
				<MicroLabel className="pt-2 pb-1.5">Product</MicroLabel>
				<MicroLabel className="pt-2 pb-1.5">Est. arrival</MicroLabel>
				<MicroLabel className="pt-2 pb-1.5 text-right">Method</MicroLabel>
				{hasActions && <span />}
				{hasAlternates && <span />}
			</div>

			{groups.map((group) => (
				<DeliveryGroup
					key={group.id}
					group={group}
					columns={columns}
					hasActions={hasActions}
					hasAlternates={hasAlternates}
				/>
			))}
		</div>
	);
}

function DeliveryGroup({
	group,
	columns,
	hasActions,
	hasAlternates,
}: {
	group: OfferDeliveryGroup;
	columns: string;
	hasActions: boolean;
	hasAlternates: boolean;
}) {
	const [open, setOpen] = useState(false);

	/* Collapsed, the group is the tier the client is on. Nothing is selected until
	   they pick, so the first tier stands in — it is the one the table reads down
	   from. */
	const lead = group.rows.find((row) => row.forSelectedTier) ?? group.rows[0];
	if (!lead) return null;

	/* The others follow the lead rather than sitting in quantity order, so opening
	   the group appends rows instead of moving the one already being read. */
	const others = group.rows.filter((row) => row.id !== lead.id);
	const label = `${others.length} other ${others.length === 1 ? 'quantity' : 'quantities'}`;

	return (
		<>
			<DeliveryRow
				row={lead}
				product={group.product}
				columns={columns}
				hasActions={hasActions}
				leading
				disclosure={
					hasAlternates ? (
						others.length > 0 ? (
							<button
								type="button"
								onClick={() => setOpen((o) => !o)}
								aria-expanded={open}
								aria-label={open ? `Hide ${label}` : `Show ${label}`}
								title={open ? `Hide ${label}` : `Show ${label}`}
								className="flex size-6 items-center justify-center rounded-[var(--r-1)] bg-transparent text-text-4 transition-colors hover:bg-surface-hover hover:text-text-1"
							>
								{open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
							</button>
						) : (
							<span />
						)
					) : null
				}
			/>

			<AnimatedHeight step={open ? 'open' : 'closed'}>
				{open &&
					others.map((row) => (
						<DeliveryRow
							key={row.id}
							row={row}
							product={group.product}
							columns={columns}
							hasActions={hasActions}
							leading={false}
							disclosure={hasAlternates ? <span /> : null}
						/>
					))}
			</AnimatedHeight>
		</>
	);
}

function DeliveryRow({
	row,
	product,
	columns,
	hasActions,
	leading,
	disclosure,
}: {
	row: OfferDeliveryRow;
	product: ProductRef;
	columns: string;
	hasActions: boolean;
	leading: boolean;
	disclosure: ReactNode;
}) {
	return (
		<div
			className={cn(
				'grid items-center gap-x-5 border-t border-line-1 py-2 transition-opacity',
				/* Dimmed, not hidden: the row prices a quantity the client might
				   still pick, so it has to stay legible. */
				row.forSelectedTier ? 'opacity-100' : 'opacity-55'
			)}
			style={{ gridTemplateColumns: columns }}
		>
			<span className="flex min-w-0 items-center gap-2">
				{/* The rows under a product's first one carry no name — they are the
				    same product at another quantity — so they are indented instead.
				    It used to be the thumbnail doing that by accident. */}
				{!leading && <span className="w-3 shrink-0" />}
				<span className="min-w-0">
					{leading && (
						<span className="block truncate text-base text-text-1">{product.name}</span>
					)}
					<span className={cn('block font-mono text-xs text-text-4', leading && 'mt-px')}>
						{row.units}
						{!row.forSelectedTier && (
							<span className="font-sans"> · if the client picks this tier</span>
						)}
					</span>
				</span>
			</span>

			{/* The badge alone. A transit window used to sit under it — "10–14 days"
			    beneath the dates those days produce — which is the same fact stated
			    twice, and under a split it was worse than redundant: the figure was
			    the option's derived span, a number nobody waits. */}
			<span className="flex min-w-0 flex-col items-start gap-1">
				{row.arrivalSlot ?? <span className="text-sm text-text-4">—</span>}
			</span>

			<span
				role={row.note ? undefined : 'radiogroup'}
				aria-label={
					row.note ? undefined : `Shipping method for ${product.name}, ${row.units}`
				}
				className={cn(
					'min-w-0',
					row.note
						? 'flex flex-wrap justify-end gap-2'
						: /* A grid, not flex-wrap: an option sized to its own label makes
						     "Standard DKK 110" and "Express DKK 1,010" two different
						     widths, and no two rows then line up. */
							'grid gap-2'
				)}
				style={
					row.note
						? undefined
						: {
								gridTemplateColumns: `repeat(${row.options.length}, minmax(0, 1fr))`,
							}
				}
			>
				{row.note ? (
					<span className="text-sm text-text-4">{row.note}</span>
				) : (
					row.options.map((option) => (
						<button
							key={option.id}
							type="button"
							role="radio"
							aria-checked={option.selected}
							disabled={!option.priced || !row.onSelect}
							title={option.disabledReason ?? undefined}
							onClick={() => row.onSelect?.(option.id)}
							className={cn(
								'flex min-w-0 items-center gap-2 rounded-[var(--r-2)] px-3 py-1.5 text-left transition-colors',
								option.priced
									? 'border border-solid'
									: 'cursor-not-allowed border border-dashed',
								!row.onSelect && 'cursor-default'
							)}
							style={{
								borderColor: option.selected
									? 'var(--accent)'
									: option.priced
										? 'var(--line-2)'
										: 'var(--line-1)',
								background: option.selected ? 'var(--accent-soft)' : 'transparent',
							}}
						>
							<RadioMark on={option.selected} muted={!option.priced} />
							{/* Truncating is the last resort, not the first: the track
							    above is sized so a real price never reaches it. But the
							    label is the only thing in the button that CAN give — the
							    radio is fixed and a clipped price would be a wrong number
							    — so it keeps `truncate`, which is also what lets it shrink
							    at all (a flex item's auto minimum is its content until
							    something hides the overflow). Without it, a price past
							    what the floor allows for pushes out through the border. */}
							<span
								className={cn(
									'flex-1 truncate text-sm',
									option.selected
										? 'font-medium text-text-1'
										: option.priced
											? 'text-text-2'
											: 'text-text-4'
								)}
							>
								{option.method}
							</span>
							<span
								className={cn(
									'shrink-0 font-mono text-sm whitespace-nowrap',
									option.priced ? 'text-text-2' : 'text-text-4'
								)}
							>
								{option.priced ? (option.price ?? '—') : 'n/a'}
								{option.supplierCost && (
									<span className="ml-1.5 font-sans text-2xs text-text-4">
										supplier {option.supplierCost}
									</span>
								)}
								{option.priceNote && (
									<span className="ml-1.5 inline-flex font-sans align-middle">
										{option.priceNote}
									</span>
								)}
							</span>
						</button>
					))
				)}

				{/* After the methods, never beside them. A split is a third option on
				    the same tier, but it is the only one with legs to state, and a
				    fourth equal column in the grid above would leave every option too
				    narrow to say "20 Express · Oct 10–11" in. */}
				{!row.note &&
					row.splits?.map((split) => (
						<SplitOption
							key={split.id}
							split={split}
							product={product}
							units={row.units}
							onSelect={row.onSelect}
						/>
					))}
			</span>

			{hasActions && <span className="flex items-center justify-end">{row.actions}</span>}
			{disclosure && <span className="flex items-center justify-end">{disclosure}</span>}
		</div>
	);
}

/**
 * A split, stated with its legs.
 *
 * The totals on the button are the option's own — the sum of the legs and the
 * span from the first out to the last in — because that is what the client is
 * agreeing to pay and wait for. The legs underneath are the detail of how, and
 * each carries its OWN arrival: the whole point of the option is that part of
 * the quantity lands earlier than the rest, and one window over both would hide
 * exactly the thing being offered.
 */
function SplitOption({
	split,
	product,
	units,
	onSelect,
}: {
	split: OfferDeliverySplitOption;
	product: ProductRef;
	units: string;
	onSelect?: (optionId: string) => void;
}) {
	/* An invalid split is not an option that happens to be unavailable — it is
	   one whose legs no longer add up, and the client is never shown it at all.
	   So it stays visible here, where it can be fixed, and refuses selection. */
	const selectable = split.valid && !!onSelect && !split.disabledReason;

	return (
		<button
			type="button"
			role="radio"
			aria-checked={split.selected}
			disabled={!selectable}
			title={split.disabledReason ?? undefined}
			onClick={() => onSelect?.(split.id)}
			className={cn(
				/* Across every method column: the grid above is sized for one-line
				   options, and a split in a single track would wrap its legs to
				   nothing. */
				'col-span-full flex min-w-0 flex-col gap-1 rounded-[var(--r-2)] border px-3 py-1.5 text-left transition-colors',
				split.valid ? 'border-solid' : 'border-dashed',
				selectable ? 'cursor-pointer' : 'cursor-default'
			)}
			style={{
				borderColor: split.selected
					? 'var(--accent)'
					: split.valid
						? 'var(--line-2)'
						: 'var(--status-warn)',
				background: split.selected ? 'var(--accent-soft)' : 'transparent',
			}}
		>
			<span className="flex min-w-0 items-center gap-2">
				<RadioMark on={split.selected} muted={!split.valid} />
				<Layers size={11} className="shrink-0 text-[var(--accent)]" />
				<span
					className={cn(
						'min-w-0 flex-1 truncate text-sm',
						split.selected ? 'font-medium text-text-1' : 'text-text-2'
					)}
				>
					{split.label}
				</span>
				{!split.valid && (
					<span className="shrink-0 text-xs text-[var(--status-warn)]">
						Needs re-split
					</span>
				)}
				<span className="shrink-0 font-mono text-sm whitespace-nowrap text-text-2">
					{split.price ?? '—'}
				</span>
			</span>

			{/* What each leg IS — how much, by what, at what price. Deliberately not
			    when it lands: no plain method states its arrival beside its price
			    either, and the Est. arrival column already carries it for whichever
			    option is selected. A split that printed its dates here said them
			    twice on the row that was picked, and showed dates on the rows that
			    weren't that no method beside them showed.
			 *
			 * The ABSENCE still reads, because it is a different fact: a leg with no
			 * lead time cannot be quoted to a client at all. */}
			<span className="flex flex-col gap-0.5 pl-[26px]">
				{split.legs.map((leg) => (
					<span
						key={leg.id}
						className="flex min-w-0 items-center gap-2 text-xs text-text-4"
					>
						<span className="shrink-0 font-mono text-text-3">{leg.quantity}</span>
						<span className="min-w-0 flex-1 truncate">
							{leg.method}
							{!leg.arrival && ' · no lead time yet'}
						</span>
						{leg.price && (
							<span className="shrink-0 font-mono whitespace-nowrap">
								{leg.price}
							</span>
						)}
						{leg.supplierCost && (
							<span className="shrink-0 whitespace-nowrap text-2xs">
								supplier {leg.supplierCost}
							</span>
						)}
						{leg.priceNote && <span className="shrink-0">{leg.priceNote}</span>}
					</span>
				))}
			</span>

			<span className="sr-only">
				Split shipping for {product.name}, {units}
				{split.arrival ? `, arriving ${split.arrival}` : ''}
			</span>
		</button>
	);
}
